/**
 * OC Message Rendering Functions
 * Provides render functions for OC-specific message UI and actions
 */

import React, { useContext } from 'react';

import { toggleSources as toggleSourcesAction } from '../../../store/transcript/actions';
import { TranscriptContext } from '../../../store/transcript/store';
import { MessageType } from '../../Message/types/messages';
import HorizontalSourceList from '../components/Sources/HorizontalSourceList';
import SourceToggleButton from '../components/Sources/SourceToggleButton';

/**
 * Render additional UI below a message (horizontal source list)
 * Shows when message has sources and they are expanded
 */
export const ocRenderAdditionalMessageUI = (message: MessageType): React.ReactNode => {
  if (!message.messageReferences || message.messageReferences.length === 0) {
    return null;
  }

  return <OcMessageSources message={message} />;
};

/**
 * Factory function to create render function for additional message UI
 * Allows configuration of whether horizontal sources should be shown
 */
export const createOcRenderAdditionalMessageUI = (showHorizontalSources: boolean = true) => {
  if (!showHorizontalSources) {
    return () => null;
  }
  return ocRenderAdditionalMessageUI;
};

/**
 * Internal component that uses TranscriptContext to render sources
 */
const OcMessageSources: React.FC<{ message: MessageType }> = ({ message }) => {
  const { state } = useContext(TranscriptContext);
  const isExpanded = state.expandedSources?.has(message.messageId) ?? false;
  const ariaId = `message-${message.messageId}-sources`;

  if (!message.messageReferences || message.messageReferences.length === 0) {
    return null;
  }

  return (
    <HorizontalSourceList
      messageId={message.messageId}
      sources={message.messageReferences}
      isExpanded={isExpanded}
      id={ariaId}
      role="region"
      aria-labelledby={`${ariaId}-toggle`}
    />
  );
};

/**
 * Render additional actions for a message (source toggle button)
 * Shows when message has sources
 */
export const ocRenderAdditionalActions = (message: MessageType): React.ReactNode[] => {
  if (!message.messageReferences || message.messageReferences.length === 0) {
    return [];
  }

  return [<OcSourceToggle key={`source-toggle-${message.messageId}`} message={message} />];
};

/**
 * Factory function to create render function for additional message actions
 * Allows configuration of whether horizontal sources toggle should be shown
 */
export const createOcRenderAdditionalActions = (showHorizontalSources: boolean = true) => {
  if (!showHorizontalSources) {
    return () => [];
  }
  return ocRenderAdditionalActions;
};

/**
 * Internal component that uses TranscriptContext to render the toggle button
 */
const OcSourceToggle: React.FC<{ message: MessageType }> = ({ message }) => {
  const { state, dispatch } = useContext(TranscriptContext);
  const isExpanded = state.expandedSources?.has(message.messageId) ?? false;
  const ariaId = `message-${message.messageId}-sources`;

  if (!message.messageReferences || message.messageReferences.length === 0) {
    return null;
  }

  const handleToggle = (messageId: string) => {
    dispatch(toggleSourcesAction(messageId));
  };

  return (
    <SourceToggleButton
      messageId={message.messageId}
      sourceCount={message.messageReferences.length}
      isExpanded={isExpanded}
      onToggle={handleToggle}
      ariaControlsId={ariaId}
      className="oc-flex-grow-1 oc-justify-content-end"
    />
  );
};

